import React from 'react';
import { IMPORT_STATUS, errorCountOf, errorLabel, userTypeLabel } from './importTotals';
import { isValidURL } from '../../utils/urlValidator';
import styles from './ImportTotalCell.module.scss';

type ImportTotalCellProps = {
  /** One entry from POST /importData/list. */
  row: Record<string, any>;
};

/**
 * The document glyph beside an error count, here and in the notification tray.
 *
 * Geometry is the design system's own `file-doc` outline, copied from
 * `public/images/file_doc.svg`, whose Figma layer name it still carries. The
 * lettering that asset draws inside the page is left out: it is illegible at
 * 14px and reads as noise rather than as "DOC".
 *
 * Inlined rather than loaded through <img> so the colour can come from CSS --
 * an <img src> cannot be recoloured, and the asset is stroked #1E2D66 while
 * this sits in the error red.
 */
export const DocumentIcon = () => (
  <svg
    className={styles.documentIcon}
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    aria-hidden="true"
    focusable="false">
    <path
      d="M13 2V6C13 8.20914 14.7909 10 17 10L21 10M3 6L3 18C3 20.2091 4.79086 22 7 22H17C19.2091 22 21 20.2091 21 18V11.6569C21 10.596 20.5786 9.57857 19.8284 8.82843L14.1716 3.17157C13.4214 2.42143 12.404 2 11.3431 2L7 2C4.79086 2 3 3.79086 3 6Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const SpinnerIcon = () => (
  <svg className={styles.spinner} viewBox="0 0 14 14" width="14" height="14" aria-hidden="true" focusable="false">
    <path
      d="M7 1.75a5.25 5.25 0 1 0 5.25 5.25"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/** The status on its own, for the records that cannot honestly say more. */
const StatusPill = ({ status }: { status: string }) => (
  <span className={status === IMPORT_STATUS.FAILED ? `${styles.status} ${styles.statusFailed}` : styles.status}>
    {status}
  </span>
);

/**
 * The Total Imported cell. Four states, but only three branches: a partial
 * import is Completed with a non-zero error count rather than a status of its
 * own, because the API's enum has no such value.
 *
 * Absent keys are how the API says "not applicable" -- an unfinished import has
 * no totalImported, and an import with nothing to report has no reportUrl -- so
 * presence is what each branch tests, never a null.
 */
export default function ImportTotalCell({ row }: ImportTotalCellProps) {
  const { status, totalRows, totalImported, targetUserRole, reportUrl, fileName } = row ?? {};

  if (status === IMPORT_STATUS.PROCESSING) {
    return (
      <span className={styles.processing}>
        <SpinnerIcon />
        Processing
      </span>
    );
  }

  // Imports that predate the counts migration recorded no row count, so the
  // status is all these records can say. The stored tally is deliberately NOT
  // shown, however large it is: in a column read as "imported out of how many"
  // a bare number answers nothing -- "1" invites "one of how many?", which is
  // exactly the figure the old path never kept and nothing can recover now.
  //
  // A zero is worse still, because "imported nobody" and "never written"
  // arrive identical. Printing the status is the narrower, honest claim, and
  // it is reliable thanks to the IMP-2112 backfill.
  if (totalRows === null || totalRows === undefined) {
    return <StatusPill status={status ?? IMPORT_STATUS.COMPLETED} />;
  }

  // A failed import records no count, and the API leaves it out on purpose:
  // the status is written alone so that "imported nobody" and "never got that
  // far" do not arrive as the same document. Reading the absent count as zero
  // here would put that distinction straight back -- "0 / 175" with "175
  // errors" claims every row was rejected, when rows that ran before the
  // import failed may already have created users, and there is no report to
  // check the claim against.
  //
  // Keyed on the missing count, not on the status alone: if the API ever does
  // record how far it got, "40 / 175" is worth showing and this branch steps
  // aside on its own.
  if (status === IMPORT_STATUS.FAILED && (totalImported === null || totalImported === undefined)) {
    return <StatusPill status={IMPORT_STATUS.FAILED} />;
  }

  const errors = errorCountOf(status, totalRows, totalImported);

  return (
    <span className={styles.cell}>
      <span className={styles.count}>
        {totalImported ?? 0} / {totalRows} {userTypeLabel(targetUserRole, totalRows)}
      </span>
      {errors > 0 &&
        (reportUrl ? (
          // A button, not an anchor: there is no href to navigate to, and a
          // span with a click handler would not be keyboard operable.
          <button
            type="button"
            className={styles.errorLink}
            aria-label={`Download error report for ${fileName}`}
            onClick={() => {
              // Same guard as the File Name cell. This is a plain render path
              // with no toaster to reject into, so warn and no-op.
              if (isValidURL(reportUrl)) {
                window.open(reportUrl, '_blank', 'noopener,noreferrer');
              } else {
                console.warn('[ImportTotalCell] Blocked unsafe reportUrl:', reportUrl);
              }
            }}>
            <DocumentIcon />
            <span className={styles.errorText}>{errorLabel(errors)}</span>
          </button>
        ) : (
          <span className={styles.errorPlain}>{errorLabel(errors)}</span>
        ))}
    </span>
  );
}
